import { mountEscapeButton } from '../escape-button';
import { pageShell, academicButton } from '../ui/components';
import { runDictation } from './dictation';
import { runVocabPhotoImport } from './vocab-photo-import';
import {
  loadVocab,
  addVocabBatch,
  saveVocab,
  toDictationItems,
  type VocabWord,
} from '../../shared/vocab-store';

/**
 * 英文單字輸入畫面(老闆/家長貼入單字,之後可拿來聽寫)。
 *
 * 介面:textarea 一行一個「english,中文」(逗號或 tab 分隔;只有 english 也容錯)
 * → 解析成 `VocabWord[]` → `addVocabBatch` → 顯示目前單字庫(可逐筆刪除)。
 * 清單上方「🎧 開始聽寫」呼叫 `runDictation`。
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕
 * `academicButton`(刪除 = danger);見 wiki page-470f59c0。
 * 學習介面要專業(boss 2026-05-23):無音效、無 mascot、無彩光、文案中性。
 * 學習/遊戲分離鐵則:只動單字資料,不碰 commanderXp / 戰鬥數值。
 */

/**
 * 解析一段貼上的多行文字成 `VocabWord[]`。
 * - 一行一個單字。
 * - english 與中文以逗號(半形/全形)或 tab 分隔;取第一個分隔點切兩半。
 * - 只有 english 也接受(zh 留空字串)。
 * - 空白行忽略。
 */
export function parseVocabText(text: string): VocabWord[] {
  const out: VocabWord[] = [];
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (line.length === 0) continue;
    // 逗號(, 或 ,)或 tab 當分隔；取第一個分隔點。
    const m = line.match(/[,，\t]/);
    let word: string;
    let zh: string;
    if (m && m.index !== undefined) {
      word = line.slice(0, m.index).trim();
      zh = line.slice(m.index + 1).trim();
    } else {
      word = line;
      zh = '';
    }
    if (word.length === 0) continue;
    out.push({ word, zh });
  }
  return out;
}

export function runVocabInput(container: HTMLElement, onDone: () => void): void {
  container.innerHTML = '';

  // 自己掛 escape 鈕以握住 unmount handle,導頁到子畫面(聽寫 / 拍單字表)前先卸載,
  // 避免堆疊兩顆「✕ 離開」(沿用全站 mountEscapeButton 慣例)。
  const unmountEscape = mountEscapeButton(onDone);

  const shell = pageShell(container, {
    title: '英文單字庫',
    subtitle: '一行一個單字,格式「english,中文」(逗號或 Tab 分隔;只填英文也可以)。',
  });

  // 既有測試 / 慣例相容:legacy class 保留(外觀由 ef- token 主導)。
  shell.root.querySelector('.ef-title')?.classList.add('vocab-input__title');
  shell.root.querySelector('.ef-subtitle')?.classList.add('vocab-input__help');

  const root = document.createElement('div');
  root.className = 'vocab-input';
  root.style.cssText = 'display:flex;flex-direction:column;gap:14px;';
  shell.body.appendChild(root);

  const textarea = document.createElement('textarea');
  textarea.className = 'vocab-input__textarea';
  textarea.setAttribute('data-testid', 'vocab-textarea');
  textarea.rows = 6;
  textarea.placeholder = 'apple,蘋果\nbanana,香蕉\ncat';
  textarea.style.cssText =
    'width:100%;box-sizing:border-box;padding:12px 14px;font-family:var(--ef-sans);' +
    'font-size:15px;line-height:1.6;color:var(--ef-ink-soft);background:var(--ef-paper-bright);' +
    'border:1px solid var(--ef-line);border-radius:var(--ef-radius-sm);resize:vertical;';
  root.appendChild(textarea);

  // ── 動作列:加入單字庫(primary)+ 拍單字表(secondary)──
  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;';
  root.appendChild(actions);

  const addBtn = academicButton({
    label: '加入單字庫',
    variant: 'primary',
    onClick: () => {
      const parsed = parseVocabText(textarea.value);
      if (parsed.length > 0) {
        addVocabBatch(parsed);
        textarea.value = '';
        renderList();
      }
    },
  });
  addBtn.classList.add('vocab-input__add');
  addBtn.setAttribute('data-testid', 'vocab-add');
  actions.appendChild(addBtn);

  const photoBtn = academicButton({
    label: '📷 拍單字表(可多張)',
    variant: 'secondary',
    onClick: () => {
      unmountEscape();
      runVocabPhotoImport(container, () => runVocabInput(container, onDone));
    },
  });
  photoBtn.classList.add('vocab-input__photo');
  photoBtn.setAttribute('data-testid', 'vocab-photo');
  actions.appendChild(photoBtn);

  // ── 單字庫清單區 ────────────────────────────────────────────────
  const listSection = document.createElement('div');
  listSection.className = 'vocab-input__list-section';
  listSection.style.cssText = 'display:flex;flex-direction:column;gap:12px;';
  root.appendChild(listSection);

  const dictateBtn = academicButton({
    label: '🎧 開始聽寫',
    variant: 'primary',
    onClick: () => {
      const items = toDictationItems(loadVocab());
      if (items.length === 0) return;
      unmountEscape();
      // 聽寫結束回到單字輸入畫面。
      runDictation(container, items, () => runVocabInput(container, onDone));
    },
  });
  dictateBtn.classList.add('vocab-input__dictate');
  dictateBtn.setAttribute('data-testid', 'vocab-dictate');
  dictateBtn.style.alignSelf = 'flex-start';
  listSection.appendChild(dictateBtn);

  const listEl = document.createElement('ul');
  listEl.className = 'vocab-input__list';
  listEl.setAttribute('data-testid', 'vocab-list');
  listEl.style.cssText = 'list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px;';
  listSection.appendChild(listEl);

  function renderList(): void {
    const list = loadVocab();
    listEl.innerHTML = '';
    dictateBtn.disabled = list.length === 0;

    if (list.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'vocab-input__empty';
      empty.setAttribute('data-testid', 'vocab-empty');
      empty.textContent = '單字庫是空的,貼上單字後按「加入單字庫」。';
      empty.style.cssText =
        'padding:14px;color:var(--ef-sub);font-size:14px;background:var(--ef-paper-bright);' +
        'border:1px dashed var(--ef-line);border-radius:var(--ef-radius-sm);';
      listEl.appendChild(empty);
      return;
    }

    list.forEach((w) => {
      const li = document.createElement('li');
      li.className = 'vocab-input__item';
      li.setAttribute('data-word', w.word);
      li.style.cssText =
        'display:flex;align-items:center;gap:12px;padding:10px 14px;background:var(--ef-paper-bright);' +
        'border:1px solid var(--ef-line);border-radius:var(--ef-radius-sm);';

      const text = document.createElement('span');
      text.className = 'vocab-input__item-text';
      text.textContent = w.zh ? `${w.word} — ${w.zh}` : w.word;
      text.style.cssText = 'flex:1;min-width:0;color:var(--ef-ink-soft);font-size:15px;';
      li.appendChild(text);

      const del = academicButton({
        label: '刪除',
        variant: 'danger',
        onClick: () => {
          const next = loadVocab().filter(
            (v) => v.word.toLowerCase() !== w.word.toLowerCase(),
          );
          saveVocab(next);
          renderList();
        },
      });
      del.classList.add('vocab-input__del');
      del.setAttribute('data-action', 'delete');
      del.setAttribute('aria-label', `刪除 ${w.word}`);
      // 清單內的刪除鈕走小尺寸,不要喧賓奪主。
      del.style.cssText = 'min-height:34px;padding:6px 16px;font-size:14px;';
      li.appendChild(del);

      listEl.appendChild(li);
    });
  }

  renderList();
}
